import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Menu, Phone, ChevronDown, LogOut, LogIn, Search, Shield, Scan, Home, Image, BookOpen, MapPin, Mail } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { LanguageSelector } from "@/components/LanguageSelector";
import { useLanguage } from "@/contexts/LanguageContext";
import { translations } from "@/translations";
import { useAuth } from "@/contexts/AuthContext";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { LocalizedLink } from "@/components/LocalizedLink";
import { HivNavButton } from "@/components/HivNavButton";

// Facebook Icon Component
const FacebookIcon = ({ className }: { className?: string }) => (
  <svg 
    className={className} 
    viewBox="0 0 24 24" 
    fill="currentColor"
  >
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

export const Header = () => {
  const { language, getLocalizedPath } = useLanguage();
  const t = translations[language];
  const { user, isAdmin, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isTreatmentsOpen, setIsTreatmentsOpen] = useState(false);
  const [isMobileTreatmentsOpen, setIsMobileTreatmentsOpen] = useState(false);
  const [isAdminMenuOpen, setIsAdminMenuOpen] = useState(false);

  // Secret keyboard shortcut for admin menu (Ctrl+Shift+A)
  useEffect(() => {
    if (!isAdmin) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'A') {
        e.preventDefault();
        setIsAdminMenuOpen(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isAdmin]);

  const scrollToSection = (sectionId: string) => {
    // Get the base path for current language
    const basePath = getLocalizedPath("/");
    const currentBasePath = location.pathname === basePath || location.pathname === "/" || 
      location.pathname === `/${language}` || location.pathname === `/${language}/`;
    
    // If we're not on the homepage, navigate there first
    if (!currentBasePath) {
      navigate(basePath, { state: { scrollTo: sectionId } });
      setIsMobileMenuOpen(false);
      setIsTreatmentsOpen(false);
      setIsMobileTreatmentsOpen(false);
      return;
    }
    
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      setIsMobileMenuOpen(false);
      setIsTreatmentsOpen(false);
      setIsMobileTreatmentsOpen(false);
    }
  };

  // Handle scroll on navigation from other pages
  useEffect(() => {
    if (location.state?.scrollTo) {
      setTimeout(() => {
        const element = document.getElementById(location.state.scrollTo);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
      // Clear the state
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const menuItems = [
    { label: t.nav.home, path: "/" },
    { label: t.nav.results, path: "/gallery" },
    { label: t.nav.blog, path: "/blog" },
    { label: t.nav.locations, sectionId: "locations" },
    { label: t.nav.contact, sectionId: "contact" },
  ];

  const treatmentCategories = [
    {
      category: 'Hair Transplant Techniques',
      items: [
        { label: 'FUE Hair Transplant', path: '/treatments/fue-hair-transplant' },
        { label: 'DHI Hair Transplant', path: '/treatments/dhi-hair-transplant' },
        { label: 'Sapphire FUE', path: '/treatments/sapphire-fue' },
        { label: 'Unshaven Transplant', path: '/treatments/unshaven-transplant' },
      ]
    },
    {
      category: 'Facial Hair Restoration',
      items: [
        { label: 'Beard Transplant', path: '/treatments/beard-transplant' },
        { label: 'Eyebrow Transplant', path: '/treatments/eyebrow-transplant' },
        { label: 'Mustache Transplant', path: '/treatments/mustache-transplant' },
      ]
    },
    {
      category: 'Revision & Correction',
      items: [
        { label: 'Failed Transplant Repair', path: '/treatments/failed-transplant-repair' },
        { label: 'Scar Revision', path: '/treatments/scar-revision' },
        { label: 'Crown Restoration', path: '/treatments/crown-restoration' },
      ]
    },
    {
      category: 'Non-Surgical Treatments',
      items: [
        { label: 'PRP Hair Treatment', path: '/treatments/prp-hair-treatment' },
        { label: 'Stem Cell Therapy', path: '/treatments/stem-cell-therapy' },
        { label: 'Hair Mesotherapy', path: '/treatments/mesotherapy' },
      ]
    },
    {
      category: 'Treatment Comparison',
      items: [
        { label: 'Compare Techniques (FUE vs DHI vs Sapphire)', path: '/compare' },
      ]
    },
    {
      category: 'Additional Services',
      items: [
        { label: 'Hair Analysis', path: '/treatments/hair-analysis' },
        { label: 'Post-Op Care', path: '/treatments/post-op-care' },
        { label: 'Vitrano Membership', path: '/treatments/vitrano-membership' },
      ]
    }
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md shadow-sm">
      {/* Top Bar - CTA Buttons with Sticky Scroll Effect */}
      <div className="border-b border-border bg-gradient-to-r from-primary/5 to-accent/5">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-12 gap-2">
            {/* Left side - Urgency indicator */}
            <div className="hidden sm:flex items-center gap-2 text-sm">
              <div className="relative">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <div className="absolute inset-0 w-2 h-2 bg-green-500 rounded-full animate-ping opacity-75" />
              </div>
              <span className="text-muted-foreground">
                {language === 'tr' ? '🔥 Bu hafta 3 randevu kaldı' : 
                 language === 'fr' ? '🔥 3 places cette semaine' :
                 language === 'nl' ? '🔥 3 plekken deze week' :
                 language === 'ar' ? '🔥 3 مواعيد هذا الأسبوع' :
                 '🔥 3 slots left this week'}
              </span>
            </div>

            {/* Right side - CTAs */}
            <div className="flex items-center gap-2 ml-auto">
              <a href="https://wa.me/31638523131" target="_blank" rel="noopener noreferrer">
                <Button 
                  variant="ghost"
                  size="sm"
                  className="flex items-center gap-2 text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-950"
                >
                  <Phone className="h-4 w-4" />
                  <span className="hidden sm:inline">WhatsApp</span>
                </Button>
              </a>
              <LocalizedLink to="/hair-analysis">
                <Button 
                  variant="secondary"
                  size="sm"
                  className="flex items-center gap-2 bg-gradient-to-r from-primary/20 to-accent/20 hover:from-primary/30 hover:to-accent/30 border-2 border-primary/50 shadow-lg hover:shadow-xl transition-all duration-300 animate-pulse hover:animate-none"
                >
                  <Scan className="h-4 w-4" />
                  <span className="hidden sm:inline">{t.nav.hairAnalysis}</span>
                </Button>
              </LocalizedLink>
              <LocalizedLink to="/book-consultation">
                <Button 
                  size="sm"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold shadow-lg"
                >
                  <span className="hidden sm:inline">{t.nav.bookConsultation}</span>
                  <span className="sm:hidden">Book</span>
                </Button>
              </LocalizedLink>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation Bar */}
      <div className="border-b border-border">
        <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <LocalizedLink 
            to="/"
            className="text-xl md:text-2xl font-bold text-foreground hover:text-primary transition-colors"
          >
            Hair Art <span className="text-primary">Clinics</span>
          </LocalizedLink>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {menuItems.slice(0, 1).map((item) => (
              item.path ? (
                <LocalizedLink
                  key={item.path}
                  to={item.path}
                  className="text-foreground hover:text-primary transition-colors font-medium"
                >
                  {item.label}
                </LocalizedLink>
              ) : (
                <button
                  key={item.sectionId}
                  onClick={() => scrollToSection(item.sectionId!)}
                  className="text-foreground hover:text-primary transition-colors font-medium"
                >
                  {item.label}
                </button>
              )
            ))}
            
            {/* Treatments Dropdown */}
            <div 
              className="relative group"
              onMouseEnter={() => setIsTreatmentsOpen(true)}
              onMouseLeave={() => setIsTreatmentsOpen(false)}
            >
              <button
                className="flex items-center gap-1 text-foreground hover:text-primary transition-colors font-medium"
              >
                {t.nav.treatments}
                <ChevronDown className={`h-4 w-4 transition-transform ${isTreatmentsOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isTreatmentsOpen && (
                <div className="absolute top-full left-0 pt-2 z-[100]">
                  <div className="bg-background border border-border rounded-lg shadow-lg py-4 min-w-[800px] px-4">
                    {/* All Treatments Link */}
                    <LocalizedLink
                      to="/treatments"
                      className="flex items-center gap-2 px-3 py-2.5 mb-3 text-sm font-semibold text-primary-foreground bg-primary hover:bg-primary/90 rounded-lg transition-colors"
                      onClick={() => setIsTreatmentsOpen(false)}
                    >
                      <span>📋</span>
                      {language === 'tr' ? 'Tüm Tedavileri Gör' : language === 'fr' ? 'Voir Tous les Traitements' : language === 'nl' ? 'Bekijk Alle Behandelingen' : language === 'ar' ? 'عرض جميع العلاجات' : 'View All Treatments'}
                    </LocalizedLink>
                    
                    <div className="grid grid-cols-3 gap-4">
                      {treatmentCategories.map((category) => (
                        <div key={category.category} className="space-y-2">
                          <h3 className="font-semibold text-sm text-primary border-b border-border pb-2">
                            {category.category}
                          </h3>
                          {category.items.map((item) => (
                            <LocalizedLink
                              key={item.path}
                              to={item.path}
                              className="block px-2 py-1.5 text-sm text-foreground hover:bg-accent rounded transition-colors"
                              onClick={() => setIsTreatmentsOpen(false)}
                            >
                              {item.label}
                            </LocalizedLink>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <HivNavButton />
            
            {menuItems.slice(1).map((item) => (
              item.path ? (
                <LocalizedLink
                  key={item.path}
                  to={item.path}
                  className="text-foreground hover:text-primary transition-colors font-medium"
                >
                  {item.label}
                </LocalizedLink>
              ) : (
                <button
                  key={item.sectionId}
                  onClick={() => scrollToSection(item.sectionId!)}
                  className="text-foreground hover:text-primary transition-colors font-medium"
                >
                  {item.label}
                </button>
              )
            ))}
          </nav>

          {/* Desktop Right Side */}
          <div className="hidden md:flex items-center gap-4">
            <LanguageSelector />
            <Button variant="ghost" size="sm" className="flex items-center gap-2" aria-label="Search the site" onClick={() => navigate(getLocalizedPath('/search'))}>
              <Search className="h-4 w-4" />
            </Button>
            {isAdmin && (
              <Popover open={isAdminMenuOpen} onOpenChange={setIsAdminMenuOpen}>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="sm" className="flex items-center gap-2" aria-label="Open admin menu">
                    <Shield className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-48" align="end">
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground mb-2">Admin Panel</p>
                    <LocalizedLink to="/admin" onClick={() => setIsAdminMenuOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start text-sm">
                        Admin Dashboard
                      </Button>
                    </LocalizedLink>
                    <LocalizedLink to="/admin/cms" onClick={() => setIsAdminMenuOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start text-sm">
                        CMS Yönetimi
                      </Button>
                    </LocalizedLink>
                    <LocalizedLink to="/bookings-calendar" onClick={() => setIsAdminMenuOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start text-sm">
                        Bookings Calendar
                      </Button>
                    </LocalizedLink>
                    <p className="text-xs text-muted-foreground pt-2 border-t">
                      Shortcut: Ctrl+Shift+A
                    </p>
                  </div>
                </PopoverContent>
              </Popover>
            )}
            {user ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={signOut}
                className="flex items-center gap-2"
              >
                <LogOut className="h-4 w-4" />
                Logout
              </Button>
            ) : (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const w = window as unknown as {
                      FB?: { login: (cb: (r: { status: string }) => void, opts?: { scope: string }) => void };
                      loadFacebookSDK?: () => void;
                    };
                    const doLogin = () => {
                      w.FB?.login((response) => {
                        if (response.status === 'connected') {
                          console.log('Facebook login successful:', response);
                        }
                      }, { scope: 'public_profile,email' });
                    };
                    if (w.FB) {
                      doLogin();
                      return;
                    }
                    document.addEventListener('fb-sdk-ready', doLogin, { once: true });
                    w.loadFacebookSDK?.();
                  }}

                  className="flex items-center gap-2 text-[#1877F2] hover:text-[#1877F2]/80 hover:bg-[#1877F2]/10"
                >
                  <FacebookIcon className="h-4 w-4" />
                  <span className="hidden lg:inline">Facebook</span>
                </Button>
                <Button variant="ghost" size="sm" className="flex items-center gap-2" onClick={() => navigate(getLocalizedPath('/auth'))}>
                  <LogIn className="h-4 w-4" />
                  Login
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button - Drawer */}
          <div className="md:hidden flex items-center gap-2">
            <LanguageSelector />
            <Drawer open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <DrawerTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon"
                  className="text-foreground hover:text-primary"
                  aria-label="Open menu"
                >
                  <Menu className="h-6 w-6" />
                </Button>
              </DrawerTrigger>
              <DrawerContent className="h-[85vh] px-0">
                <DrawerHeader className="border-b border-border/50 pb-4">
                  <DrawerTitle className="text-2xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                    Hair Art Clinics
                  </DrawerTitle>
                </DrawerHeader>
                
                <div className="overflow-y-auto flex-1 py-4">
                  <nav className="flex flex-col space-y-1 px-4">
                    {/* Quick Actions */}
                    <div className="mb-4 space-y-2">
                      <Button className="w-full justify-start gap-3 h-12" variant="secondary" onClick={() => { setIsMobileMenuOpen(false); navigate(getLocalizedPath('/search')); }}>
                        <Search className="h-5 w-5" />
                        <span className="font-semibold">Arama</span>
                      </Button>
                      <LocalizedLink to="/hair-analysis" onClick={() => setIsMobileMenuOpen(false)}>
                        <Button className="w-full justify-start gap-3 h-12 bg-gradient-to-r from-primary/90 to-accent/90 hover:from-primary hover:to-accent text-primary-foreground shadow-lg">
                          <Scan className="h-5 w-5" />
                          <span className="font-semibold">{t.nav.hairAnalysis}</span>
                        </Button>
                      </LocalizedLink>
                      <LocalizedLink to="/book-consultation" onClick={() => setIsMobileMenuOpen(false)}>
                        <Button className="w-full justify-start gap-3 h-12" variant="outline">
                          <Phone className="h-5 w-5" />
                          <span className="font-semibold">{t.nav.bookConsultation}</span>
                        </Button>
                      </LocalizedLink>
                    </div>

                    {/* Main Navigation */}
                    <div className="space-y-1">
                      <LocalizedLink
                        to="/"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-accent/50 transition-colors"
                      >
                        <Home className="h-5 w-5 text-primary" />
                        <span className="font-medium">{t.nav.home}</span>
                      </LocalizedLink>

                      {/* Treatments - Collapsible */}
                      <Collapsible
                        open={isMobileTreatmentsOpen}
                        onOpenChange={setIsMobileTreatmentsOpen}
                      >
                        <CollapsibleTrigger className="w-full flex items-center justify-between px-4 py-3 rounded-lg hover:bg-accent/50 transition-colors">
                          <div className="flex items-center gap-3">
                            <BookOpen className="h-5 w-5 text-primary" />
                            <span className="font-medium">{t.nav.treatments}</span>
                          </div>
                          <ChevronDown className={`h-4 w-4 transition-transform ${isMobileTreatmentsOpen ? 'rotate-180' : ''}`} />
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="mt-2 ml-8 space-y-3 pr-4">
                            {/* All Treatments Link - Mobile */}
                            <LocalizedLink
                              to="/treatments"
                              className="flex items-center gap-2 px-3 py-2.5 text-sm font-semibold text-primary-foreground bg-primary hover:bg-primary/90 rounded-lg transition-colors"
                              onClick={() => {
                                setIsMobileTreatmentsOpen(false);
                                setIsMobileMenuOpen(false);
                              }}
                            >
                              <span>📋</span>
                              {language === 'tr' ? 'Tüm Tedavileri Gör' : language === 'fr' ? 'Voir Tous les Traitements' : language === 'nl' ? 'Bekijk Alle Behandelingen' : language === 'ar' ? 'عرض جميع العلاجات' : 'View All Treatments'}
                            </LocalizedLink>
                            
                            {treatmentCategories.map((category) => (
                              <div key={category.category} className="space-y-1">
                                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-2 py-1">
                                  {category.category}
                                </h4>
                                <div className="space-y-0.5">
                                  {category.items.map((item) => (
                                    <LocalizedLink
                                      key={item.path}
                                      to={item.path}
                                      className="block text-sm px-2 py-2 rounded-md hover:bg-accent/30 transition-colors"
                                      onClick={() => {
                                        setIsMobileTreatmentsOpen(false);
                                        setIsMobileMenuOpen(false);
                                      }}
                                    >
                                      {item.label}
                                    </LocalizedLink>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>

                      <div className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-accent/50 transition-colors">
                        <span className="h-5 w-5" aria-hidden="true" />
                        <HivNavButton className="text-left" />
                      </div>

                      <LocalizedLink
                        to="/gallery"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-accent/50 transition-colors"
                      >
                        <Image className="h-5 w-5 text-primary" />
                        <span className="font-medium">{t.nav.results}</span>
                      </LocalizedLink>

                      <LocalizedLink
                        to="/blog"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-accent/50 transition-colors"
                      >
                        <BookOpen className="h-5 w-5 text-primary" />
                        <span className="font-medium">{t.nav.blog}</span>
                      </LocalizedLink>


                      <button
                        onClick={() => {
                          scrollToSection("locations");
                          setIsMobileMenuOpen(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-accent/50 transition-colors"
                      >
                        <MapPin className="h-5 w-5 text-primary" />
                        <span className="font-medium">{t.nav.locations}</span>
                      </button>

                      <button
                        onClick={() => {
                          scrollToSection("contact");
                          setIsMobileMenuOpen(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-accent/50 transition-colors"
                      >
                        <Mail className="h-5 w-5 text-primary" />
                        <span className="font-medium">{t.nav.contact}</span>
                      </button>
                    </div>

                    {/* Admin Section */}
                    {isAdmin && (
                      <div className="mt-4 pt-4 border-t border-border/50 space-y-1">
                        <div className="px-4 py-2">
                          <p className="text-xs font-semibold text-muted-foreground uppercase">Admin</p>
                        </div>
                        <LocalizedLink
                          to="/admin"
                          onClick={() => setIsMobileMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-accent/50 transition-colors"
                        >
                          <Shield className="h-5 w-5 text-primary" />
                          <span className="font-medium">Admin Dashboard</span>
                        </LocalizedLink>
                        <LocalizedLink
                          to="/bookings-calendar"
                          onClick={() => setIsMobileMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-accent/50 transition-colors"
                        >
                          <Shield className="h-5 w-5 text-primary" />
                          <span className="font-medium">Bookings Calendar</span>
                        </LocalizedLink>
                      </div>
                    )}

                    {/* Auth Section */}
                    <div className="mt-4 pt-4 border-t border-border/50">
                      {user ? (
                        <button
                          onClick={() => {
                            signOut();
                            setIsMobileMenuOpen(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-accent/50 transition-colors text-left"
                        >
                          <LogOut className="h-5 w-5 text-primary" />
                          <span className="font-medium">Logout</span>
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => { setIsMobileMenuOpen(false); navigate(getLocalizedPath('/auth')); }}
                          className="flex w-full items-center gap-3 px-4 py-3 rounded-lg hover:bg-accent/50 transition-colors"
                        >
                          <LogIn className="h-5 w-5 text-primary" />
                          <span className="font-medium">Login</span>
                        </button>
                      )}
                    </div>
                  </nav>
                </div>
              </DrawerContent>
            </Drawer>
          </div>
        </div>
      </div>
      </div>
    </header>
  );
};
